import React, { useContext, useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import VisuallyHidden from '@wix/da-a11y/pkg/components/VisuallyHidden';
import Heading from '@wix/da-a11y/pkg/components/headings/Heading';
import { BiData } from '@wix/da-bi/pkg/events';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { useFlag } from '@wix/da-react-context/pkg/flags/hooks';
import PlainButtonOrLink from '@wix/da-ds/pkg/Buttons/PlainButtonOrLink';
import FoldableTagList from '@wix/da-ds/pkg/TagList/FoldableTagList';
import Viewer from '../../../DaEditorWrapper/Viewer/Deviation';
import { useSubscriptionTier } from '../../../utils/hooks/useSubscriptionTier';
import CollapsibleBlock from '../../../CollapsibleBlock';
import License from '../../../License';
import TextRenderer from '../../../TextRenderer/TextRenderer';
import MatureIndicator from '../../../SubmissionDetails/_partials/MatureIndicator';
import StatBlock from '../_partials/StatBlock';
import Subjects from '../_partials/Subjects';
import DetailItemList from './DetailItemList';
import NftDetails from '../../../Nft/NftDetails';
import DreamupInfo from './DreamupInfo';
import defaultStyles from './Default.scss';
const DESCRIPTION_COLLAPSE_HEIGHT = 130;
export const ExtendedDetails = ({ deviation , deviation: { deviationId , author , publishedTime , isMature , isDreamsofart , extended: { subjectTags , locationTag , tags , license , descriptionText , nfts , dreamsofart  } = {}  }  })=>{
    var _descriptionText_html;
    const { t  } = useTranslation();
    const isMobile = useContext(MobileContext);
    const newCoreSymbols = useFlag('new_core_symbols');
    const descriptionRef = useRef(null);
    const [collapsedDescription, setCollapsedDescription] = useState();
    const { isSubscribedButBlocked  } = useSubscriptionTier(deviation);
    const isDescriptionTextShown = !!(descriptionText === null || descriptionText === void 0 ? void 0 : (_descriptionText_html = descriptionText.html) === null || _descriptionText_html === void 0 ? void 0 : _descriptionText_html.markup) && !isSubscribedButBlocked;
    const areDetailItemsShown = !isSubscribedButBlocked && !isDreamsofart;
    // On mobile, collapse description block if height is over the threshold
    // deviationId is in deps to make sure we run this again if deviation changes due to duperbrowse
    useEffect(()=>{
        var _descriptionRef_current;
        var _descriptionRef_current_clientHeight;
        if (isMobile && ((_descriptionRef_current_clientHeight = (_descriptionRef_current = descriptionRef.current) === null || _descriptionRef_current === void 0 ? void 0 : _descriptionRef_current.clientHeight) !== null && _descriptionRef_current_clientHeight !== void 0 ? _descriptionRef_current_clientHeight : 0) > DESCRIPTION_COLLAPSE_HEIGHT) {
            setCollapsedDescription(true);
        }
    }, [
        isMobile,
        deviationId
    ]);
    return /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement(StatBlock, {
        deviation: deviation,
        className: defaultStyles['badges-and-stats']
    }), tags && /*#__PURE__*/ React.createElement("div", {
        className: defaultStyles['tags']
    }, /*#__PURE__*/ React.createElement(FoldableTagList, {
        tags: tags,
        moreLabel: t('common.more'),
        lessLabel: t('common.less')
    })), /*#__PURE__*/ React.createElement(Subjects, {
        className: defaultStyles['subject'],
        subjectTags: subjectTags,
        locationTag: locationTag
    }), isDescriptionTextShown && /*#__PURE__*/ React.createElement("div", {
        className: cn(isMobile && defaultStyles['collapsible-description'], defaultStyles['description-wrapper'], collapsedDescription === true && defaultStyles['collapsed-description'], collapsedDescription === false && defaultStyles['expanded-description']),
        id: "description"
    }, /*#__PURE__*/ React.createElement(VisuallyHidden, null, /*#__PURE__*/ React.createElement(Heading, null, t('deviation.details.description'))), /*#__PURE__*/ React.createElement(TextRenderer, {
        className: defaultStyles['description'],
        textContent: descriptionText,
        legacyTheme: "extended-details",
        reactViewer: Viewer,
        ref: descriptionRef
    }), isMobile && collapsedDescription && /*#__PURE__*/ React.createElement("div", {
        className: defaultStyles['expand-description']
    }, /*#__PURE__*/ React.createElement(PlainButtonOrLink, {
        onClick: ()=>setCollapsedDescription(false),
        biData: BiData({
            evid: 2,
            click_info: 'dev_page_description_expand'
        }),
        "aria-controls": 'description',
        "aria-expanded": collapsedDescription
    }, t('deviation.details.showFullDescription')))), areDetailItemsShown && /*#__PURE__*/ React.createElement(CollapsibleBlock, {
        buttonTextCollapsed: t('deviation.details.more'),
        buttonTextExpanded: t('deviation.details.less'),
        maxHeight: isMobile ? 120 : 160,
        buttonClassName: defaultStyles['collapse-button'],
        className: defaultStyles['collapsible-block'],
        shutterBlockClassName: defaultStyles['collapsible-content'],
        fadeClassName: defaultStyles['collapsible-fade'],
        showFade: true
    }, /*#__PURE__*/ React.createElement("div", null, /*#__PURE__*/ React.createElement(DetailItemList, {
        deviation: deviation
    }))), isMature && /*#__PURE__*/ React.createElement(MatureIndicator, {
        deviation: deviation
    }), nfts && nfts.map((nft)=>/*#__PURE__*/ React.createElement(NftDetails, {
            key: nft.nftId,
            deviation: deviation,
            nft: nft
        })), isDreamsofart ? /*#__PURE__*/ React.createElement(DreamupInfo, {
        className: defaultStyles['dreamup'],
        prompt: dreamsofart === null || dreamsofart === void 0 ? void 0 : dreamsofart.prompt
    }) : /*#__PURE__*/ React.createElement("div", {
        className: defaultStyles['license']
    }, /*#__PURE__*/ React.createElement(License, {
        license: license,
        author: author,
        publishedTime: publishedTime,
        showSymbol: !newCoreSymbols
    })));
};
ExtendedDetails.displayName = 'ExtendedDetails';
export default ExtendedDetails;
